import React, { ReactNode, useMemo } from "react";
import { BreadCrumb as PrimeBreadcrumb, type BreadCrumbProps as PrimeBreadcrumbProps } from "primereact/breadcrumb";
import type { MenuItem } from "primereact/menuitem";
import { useNavigate } from "react-router-dom";
import { Button } from "../Button/Button";
import { IconButton } from "../IconButton/IconButton";

import styles from "./Breadcrumb.module.css";

const mergeClassNames = (...values: Array<string | false | undefined>): string =>
  values.filter(Boolean).join(" ");

export type BreadcrumbItem = MenuItem;

export interface BreadcrumbProps extends Omit<PrimeBreadcrumbProps, "model" | "home" | "separator"> {
  items: BreadcrumbItem[];
  home?: BreadcrumbItem;
}

const DISABLED_PROP = true;

const renderIcon = (icon?: ReactNode): ReactNode => {
  if (typeof icon === "string") {
    return (
      <span
        className="material-symbols-outlined"
        aria-hidden="true"
        data-icon-usage="listing"
        data-icon-size="sm"
        data-icon-state="default"
      >
        {icon}
      </span>
    );
  }

  return icon ?? undefined;
};

export const Breadcrumb: React.FC<BreadcrumbProps> = ({ items, home, className, ...rest }) => {
  const navigate = useNavigate();

  const normalizedItems = useMemo<MenuItem[]>(
    () =>
      items.map((item, index) => {
        const isLast = index === items.length - 1;
        
        return {
          ...item,
          icon: renderIcon(item.icon),
          template: item.template || ((p, options) => {
             if (isLast) {
               return (
                 <li className={options.className}>
                   <span className={styles.lastItem}>{item.label}</span>
                 </li>
               );
             }

             // Crumb sem tela própria (grupo): texto simples, não-clicável.
             if (item.disabled) {
               return (
                 <li className={options.className}>
                   <span className={styles.groupItem}>{item.label}</span>
                 </li>
               );
             }

             const handleClick = (e: React.MouseEvent): void => {
               e.preventDefault();
               if (item.command) {
                 item.command({ originalEvent: e, item: p });
               } else if (item.url) {
                 navigate(item.url);
               }
             };

             const content = (
               <Button
                 variant="text"
                 severity="secondary"
                 label={item.label}
                 icon={typeof item.icon === "string" ? item.icon : undefined}
                 disabled={item.disabled}
                 className={styles.itemBtn}
                 onClick={handleClick}
               />
             );

             return (
               <li className={options.className}>
                 {content}
               </li>
             );
          }),
          disabled: isLast ? DISABLED_PROP : item.disabled
        }
      }),
    [items, navigate]
  );

  const homeItem = useMemo<MenuItem | undefined>(() => {
    if (!home) return undefined;

    const handleClick = (e: React.MouseEvent): void => {
      e.preventDefault();
      if (home.command) {
        home.command({ originalEvent: e, item: {} });
      } else if (home.url) {
        navigate(home.url);
      }
    };

    const content = (
      <IconButton
        variant="text"
        severity="secondary"
        icon={typeof home.icon === "string" ? home.icon : "home"}
        disabled={home.disabled}
        className={styles.homeBtn}
        aria-label={home.label || "Home"}
        onClick={handleClick}
      />
    );

    return {
      ...home,
      icon: renderIcon(home.icon),
      className: mergeClassNames(styles.home, home.className),
      template: home.template || ((_, options) => {
        return (
          <li className={options.className}>
            {content}
          </li>
        );
      })
    };
  }, [home, navigate]);

  return (
    <PrimeBreadcrumb
      {...rest}
      model={normalizedItems}
      home={homeItem}
      className={mergeClassNames(styles.root, className)}
    />
  );
};

export default Breadcrumb;
